/* Copyright (c) 2020-present CloudIO Inc */

import { selector, useStateXSelector } from '@cloudio-saas/statex';
import { useRowIndex, useStaticRuntime } from 'common/Context';
import { DevProps, ItemID } from 'componentregistry';
import { Platform } from 'componentregistry/platform/Platform';
import { useCurrentDataStore } from 'components/DataStoreReference/DataStoreReference';
import {
  activeSidebarPanelAtom,
  contextMenuAtom,
  getDataStoreAlias,
} from 'layout/Sidebar/atoms';
import { TreeTypeDesigner } from 'layout/Sidebar/panels/TreeView/state';
import { usePlatform } from 'platform/PlatformHooks';
import { isDeveloper, userAtom } from 'root/useUser';
import { useUpdateOnHMR } from 'utils/hmr';
import { componentTipAtom } from 'utils/state';
import { activeItemSelector } from './activeItemSelector';
import {
  developerModeAtom,
  dragItemAtom,
  dragOverItemIdsAtom,
  hoverItemsAtom,
} from './atoms';

const emptyDevProps = {};

export const MISSING_ID = '_MISSING_ID_';

export const devPropsSelector = selector<DevProps, Platform>({
  path: [
    'transient',
    'designer',
    'DevProps',
    ':appUid',
    ':pageId',
    ':itemId',
    ':dsAlias',
    ':rowIndex',
  ],
  get: ({ get, getOnce, set, params }, platform) => {
    const { appUid, pageId, itemId, dsAlias, rowIndex } = params as Record<
      string,
      string
    >;
    if (
      !itemId ||
      itemId === MISSING_ID ||
      !get(developerModeAtom, { params: { appUid } })
    ) {
      return emptyDevProps;
    }
    const user = getOnce(userAtom);
    const showComponentTip = get(componentTipAtom, {
      params: { userName: user?.userName ?? 'x' },
    });
    const developer = isDeveloper(platform, appUid);
    const onClick = (e: React.MouseEvent<HTMLElement>) => {
      e.stopPropagation();
      e.preventDefault();
      if (!developer) {
        return;
      }
      const activeCode = getOnce(activeSidebarPanelAtom);
      if (activeCode && !['tree', 'props'].includes(activeCode)) {
        set(activeSidebarPanelAtom, 'tree');
      }
      set(
        activeItemSelector,
        { appUid, pageId, itemId, expand: true, treename: TreeTypeDesigner },
        {
          params: { appUid, treename: TreeTypeDesigner, pageId },
          props: platform,
        },
      );
    };

    const onContextMenu = (e: React.MouseEvent<HTMLElement>) => {
      e.stopPropagation();
      e.preventDefault();
      set(
        activeItemSelector,
        { appUid, pageId, itemId, expand: false, treename: TreeTypeDesigner },
        {
          params: { appUid, treename: TreeTypeDesigner, pageId },
          props: platform,
        },
      );
      set(contextMenuAtom, {
        appUid,
        pageId,
        itemId,
        left: e.clientX,
        top: e.clientY,
        orphan: false,
        dsAlias,
      });
    };

    const onMouseEnter = () => {
      if (getOnce(dragItemAtom)) {
        set(dragOverItemIdsAtom, (stack) => {
          const s = [...stack];
          s.push(itemId);
          return s;
        });
      } else if (showComponentTip) {
        set(hoverItemsAtom, (stack) => {
          const s = [...stack];
          s.push({ itemId, pageId, rowIndex: Number(rowIndex) });
          return s;
        });
      }
    };

    const onMouseLeave = () => {
      if (getOnce(dragItemAtom)) {
        set(dragOverItemIdsAtom, (stack) => {
          const s = [...stack];
          s.pop();
          return s;
        });
      } else if (showComponentTip) {
        set(hoverItemsAtom, (stack) => {
          const s = [...stack];
          s.pop();
          return s;
        });
      }
    };

    return {
      onClick,
      onContextMenu,
      onMouseEnter,
      onMouseLeave,
    };
  },
});

export default function useDesignerLite(
  appUid: string,
  pageId: ItemID,
  itemId: ItemID,
): DevProps {
  useUpdateOnHMR();
  const platform = usePlatform();
  const item = platform.getItem(appUid, pageId, itemId);
  const store = useCurrentDataStore();
  const dsAlias = ((item && getDataStoreAlias(item)) || store?.alias) ?? '';
  const rowIndex = useRowIndex();

  const props = useStateXSelector(devPropsSelector, {
    params: { appUid, pageId, itemId, dsAlias, rowIndex: rowIndex ?? -1 },
    props: platform,
  });

  const staticRuntime = useStaticRuntime();
  return staticRuntime ? {} : props;
}
